/**
 * useAudioVisualization Hook
 * Manages audio waveform visualization using Web Audio API and Canvas
 * Provides real-time frequency visualization and waveform data capture
 */

import { useCallback, useEffect, useRef } from 'react';

interface UseAudioVisualizationOptions {
  analyserNode: AnalyserNode | null;
  isRecording: boolean;
  barColor?: string;
  backgroundColor?: string;
}

interface UseAudioVisualizationReturn {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  waveformSamplesRef: React.MutableRefObject<number[]>;
}

export function useAudioVisualization(options: UseAudioVisualizationOptions): UseAudioVisualizationReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | undefined>(undefined);
  const waveformSamplesRef = useRef<number[]>([]);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !options.analyserNode || !dataArrayRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const analyser = options.analyserNode;
    const dataArray = dataArrayRef.current;

    // Get frequency data
    if (dataArray) {
      analyser.getByteFrequencyData(dataArray);
    } else {
      return;
    }

    // Calculate average amplitude for waveform capture (0-255 range)
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const avgAmplitude = sum / dataArray.length;

    // Convert 0-255 range to 0-120 dB scale for storage
    // 255 in frequency data ≈ max amplitude, map to 120 dB
    const dbValue = (avgAmplitude / 255) * 120;

    // Store sample for final waveform (capture every frame)
    waveformSamplesRef.current.push(dbValue);

    // Clear canvas
    ctx.fillStyle = options.backgroundColor ?? '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate dynamic bar count based on canvas width
    // Each bar: 3px width + 1px gap = 4px total
    const barWidth = 2;
    const barGap = 1;
    const barCount = Math.floor(canvas.width / (barWidth + barGap));

    // Downsample to calculated bar count for consistent visualization
    const downsample = (data: Uint8Array, targetLength: number): number[] => {
      if (data.length <= targetLength) return Array.from(data);

      const result = new Array(targetLength);
      const blockSize = data.length / targetLength;

      for (let i = 0; i < targetLength; i++) {
        const start = Math.floor(i * blockSize);
        const end = Math.floor((i + 1) * blockSize);
        let sum = 0;
        for (let j = start; j < end; j++) {
          sum += data[j];
        }
        result[i] = Math.round(sum / (end - start));
      }

      return result;
    };

    const sampledData = downsample(dataArray, barCount);

    const baseLineY = canvas.height - 2; // Base line near bottom

    // Draw base line
    const primaryColor =
      options.barColor ?? (getComputedStyle(canvas).getPropertyValue('--mui-palette-primary-main') || '#1976d2');
    ctx.fillStyle = primaryColor;
    ctx.fillRect(0, baseLineY, canvas.width, 2);

    // Draw bars upward from base line
    for (let i = 0; i < barCount; i++) {
      const value = sampledData[i];
      const normalizedValue = value / 255;
      const maxBarHeight = canvas.height - 4; // Leave space for base line
      const minBarHeight = 2; // Minimum bar height for visibility
      const barHeight = Math.max(minBarHeight, normalizedValue * maxBarHeight);

      const x = i * (barWidth + barGap);
      const y = baseLineY - barHeight; // Start from base line, go up

      // Use primary color gradient (bottom to top)
      const gradient = ctx.createLinearGradient(0, baseLineY, 0, y);
      const primaryLightColor = getComputedStyle(canvas).getPropertyValue('--mui-palette-primary-light') || '#42a5f5';
      gradient.addColorStop(0, primaryColor);
      gradient.addColorStop(1, primaryLightColor);

      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, barWidth, barHeight);
    }

    animationFrameRef.current = requestAnimationFrame(drawWaveform);
  }, [options.analyserNode, options.barColor, options.backgroundColor]);

  const startVisualization = useCallback(() => {
    if (!options.analyserNode || animationFrameRef.current) return;

    // Initialize data array if needed
    if (!dataArrayRef.current) {
      dataArrayRef.current = new Uint8Array(options.analyserNode.frequencyBinCount);
    }

    drawWaveform();
  }, [drawWaveform, options.analyserNode]);

  const stopVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (options.isRecording) {
      startVisualization();
    } else {
      stopVisualization();
    }
    return stopVisualization;
  }, [options.isRecording, startVisualization, stopVisualization]);

  // Resize canvas to match display size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return { canvasRef, waveformSamplesRef };
}
